// Tenant-scoped app paths. Each business is a separate installable app, so its pages must live under a
// path of its own: /b/<slug>/ for clients and /t/<slug>/admin|barbeiro for the panels. The React Router
// basename is what keeps every internal link working untouched.
const PANEL_RE = /^\/t\/([a-z0-9-]+)(?=\/|$)/;
const PUBLIC_RE = /^\/b\/([a-z0-9-]+)(?=\/|$)/;

export function panelTenant(pathname = window.location.pathname) {
  const m = pathname.match(PANEL_RE);
  return m ? m[1] : null;
}

export function routerBasename(pathname = window.location.pathname) {
  const slug = panelTenant(pathname);
  return slug ? `/t/${slug}` : "/";
}

/** Scope of the app that owns the current page. Never "/" — that is what captured other businesses. */
export function appScope(pathname = window.location.pathname) {
  const pub = pathname.match(PUBLIC_RE);
  if (pub) return { scope: `/b/${pub[1]}/`, slug: pub[1], start: "public" };
  const slug = panelTenant(pathname);
  if (!slug) return null;
  const rest = pathname.slice(`/t/${slug}`.length);
  if (rest.startsWith("/barbeiro")) return { scope: `/t/${slug}/barbeiro/`, slug, start: "barber" };
  if (rest.startsWith("/admin")) return { scope: `/t/${slug}/admin/`, slug, start: "admin" };
  return null;
}

/** Login route INSIDE the current app scope (router-relative, so the basename applies). Leaving the
 * scope makes Chrome open the installed app in a browser tab with the URL bar on top. */
export function loginRoute(pathname = window.location.pathname) {
  const own = appScope(pathname);
  if (own?.start === "admin") return "/admin/login";
  if (own?.start === "barber") return "/barbeiro/login";
  return "/login";
}

/** Absolute version of loginRoute(), for window.location navigations. */
export function loginUrl(pathname = window.location.pathname) {
  const own = appScope(pathname);
  return own && own.start !== "public" ? `/t/${own.slug}${loginRoute(pathname)}` : "/login";
}

/** Scope of the app the window was LAUNCHED from (standalone). Lets us detect that the user is browsing
 * a business inside the installed app of another one — typical of legacy installs with scope "/". */
export function launchScope() {
  const path = sessionStorage.getItem("bf:launch_path");
  return path ? appScope(path) : null;
}

export function rememberLaunchPath() {
  const standalone = window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;
  if (standalone && !sessionStorage.getItem("bf:launch_path")) {
    sessionStorage.setItem("bf:launch_path", window.location.pathname);
  }
}

/** Business remembered for this device: localStorage first, then the cookie inherited by iOS web apps. */
export function rememberedBusiness() {
  const slug = localStorage.getItem("bf:biz_slug");
  if (slug) return { slug, start: localStorage.getItem("bf:biz_start") || "public", name: localStorage.getItem("bf:biz_name") || "" };
  const raw = document.cookie.split("; ").find((c) => c.startsWith("bf_biz="));
  if (!raw) return null;
  const [cs, start, name] = decodeURIComponent(raw.slice(7)).split("|");
  return cs ? { slug: cs, start: start || "public", name: name || "" } : null;
}

/** When running installed (standalone) on an address with no app scope — typical of legacy installs
 * whose scope was "/" — this is the tenant path we should recover to. */
export function installedTarget() {
  const standalone = window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;
  const biz = rememberedBusiness();
  if (!standalone || !biz) return null;
  if (biz.start === "admin") return `/t/${biz.slug}/admin/`;
  if (biz.start === "barber") return `/t/${biz.slug}/barbeiro/`;
  return `/b/${biz.slug}/`;
}

/** True when the installed app running this window belongs to another business/role. */
export function foreignApp(pathname = window.location.pathname) {
  const standalone = window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;
  if (!standalone) return false;
  const own = appScope(pathname);
  const launched = launchScope();
  return !own || !launched || launched.scope !== own.scope;
}

/** Sends legacy /admin and /barbeiro URLs (and the old ?biz= ones) to the path of the business. */
export function ensureTenantPath(slug) {
  if (!slug || panelTenant()) return false;
  const { pathname, search, hash } = window.location;
  const clean = new URLSearchParams(search);
  clean.delete("biz");
  const qs = clean.toString();
  window.location.replace(`/t/${slug}${pathname}${qs ? `?${qs}` : ""}${hash}`);
  return true;
}

/** Leaves the tenant app (login, landing, super admin): must bypass the router basename. */
export function leaveApp(path, replace = false) {
  if (replace) window.location.replace(path);
  else window.location.assign(path);
}
